import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search,
  Database,
  Tag,
  Code,
  BookOpen,
  FileText,
  Loader2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  searchKnowledge,
  getKnowledgeOverview,
  type KnowledgeSearchResult,
} from '@/api/knowledge';

interface KnowledgeViewerProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Helper to safely display any value that may be an object (e.g., translations like {en_US, es_MX})
function safeDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    // Handle translation objects - prefer en_US, then es_MX, then first value
    const obj = value as Record<string, unknown>;
    if ('en_US' in obj) return String(obj.en_US ?? '');
    if ('es_MX' in obj) return String(obj.es_MX ?? '');
    const firstValue = Object.values(obj)[0];
    if (firstValue !== undefined) return safeDisplayValue(firstValue);
    return JSON.stringify(value);
  }
  return String(value);
}

const TYPE_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  model: { icon: Database, label: 'Model', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  field: { icon: Tag, label: 'Field', color: 'bg-green-100 text-green-700 border-green-200' },
  pattern: { icon: FileText, label: 'Pattern', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  terminology: { icon: BookOpen, label: 'Term', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  code: { icon: Code, label: 'Code', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

function ResultCard({ result, isExpanded, onToggle }: {
  result: KnowledgeSearchResult;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const config = TYPE_CONFIG[result.type] || TYPE_CONFIG.field;
  const Icon = config.icon;

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-muted/50 transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", config.color)}>
          <Icon className="h-3 w-3 mr-1" />
          {config.label}
        </Badge>
        <span className="flex-1 text-sm font-medium truncate">
          {result.metadata.model_name && result.metadata.field_name
            ? `${safeDisplayValue(result.metadata.model_name)}.${safeDisplayValue(result.metadata.field_name)}`
            : safeDisplayValue(result.metadata.model_name) || safeDisplayValue(result.metadata.source)?.substring(0, 50) || result.id}
        </span>
        <span className="text-xs text-muted-foreground font-mono">
          {(result.score * 100).toFixed(1)}%
        </span>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t bg-muted/30">
          <div className="space-y-2 text-xs">
            {result.type === 'field' && (
              <>
                <div className="flex gap-4">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-mono">{safeDisplayValue(result.metadata.field_type)}</span>
                </div>
                {result.metadata.field_label && (
                  <div className="flex gap-4">
                    <span className="text-muted-foreground">Label:</span>
                    <span>{safeDisplayValue(result.metadata.field_label)}</span>
                  </div>
                )}
                {result.metadata.relation && (
                  <div className="flex gap-4">
                    <span className="text-muted-foreground">Relation:</span>
                    <span className="font-mono">{safeDisplayValue(result.metadata.relation)}</span>
                  </div>
                )}
                {result.metadata.required && (
                  <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                    Required
                  </Badge>
                )}
              </>
            )}
            {result.type === 'pattern' && result.metadata.pattern_sql && (
              <div>
                <span className="text-muted-foreground block mb-1">SQL Pattern:</span>
                <pre className="bg-background p-2 rounded text-[11px] overflow-x-auto border">
                  {safeDisplayValue(result.metadata.pattern_sql)}
                </pre>
              </div>
            )}
            {result.metadata.source && (
              <div>
                <span className="text-muted-foreground block mb-1">Indexed Content:</span>
                <pre className="bg-background p-2 rounded text-[11px] overflow-x-auto border whitespace-pre-wrap">
                  {safeDisplayValue(result.metadata.source)}
                </pre>
              </div>
            )}
            <div className="text-[10px] text-muted-foreground pt-1">
              ID: {result.id}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function KnowledgeViewer({ projectId, open, onOpenChange }: KnowledgeViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // Get overview stats
  const { data: overview, isLoading: isLoadingOverview } = useQuery({
    queryKey: ['knowledgeOverview'],
    queryFn: getKnowledgeOverview,
    enabled: open,
  });

  // Search results
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['knowledgeSearch', projectId, activeSearch],
    queryFn: () => searchKnowledge(projectId, activeSearch, 50),
    enabled: open && activeSearch.length > 0,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(searchQuery);
    setExpandedIds(new Set());
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredResults = searchResults?.results.filter(
    r => !typeFilter || r.type === typeFilter
  ) || [];

  // Count by type
  const typeCounts = searchResults?.results.reduce((acc, r) => {
    acc[r.type] = (acc[r.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Knowledge Base Viewer
          </DialogTitle>
        </DialogHeader>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm">
          {isLoadingOverview ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <span className="text-muted-foreground">
                <strong className="text-foreground">{overview?.vectorCount.toLocaleString()}</strong> vectors indexed
              </span>
              <span className="text-muted-foreground">
                Project: <strong className="text-foreground font-mono">{projectId}</strong>
              </span>
            </>
          )}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search the knowledge base (e.g. 'sales order revenue', 'partner customer')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" disabled={!searchQuery.trim() || isSearching}>
            {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </form>

        {/* Results */}
        {activeSearch && (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Type filter tabs */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Button
                variant={typeFilter === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter(null)}
                className="h-7 text-xs"
              >
                All ({searchResults?.results.length || 0})
              </Button>
              {Object.entries(TYPE_CONFIG).map(([type, config]) => {
                const count = typeCounts[type] || 0;
                if (count === 0) return null;
                const Icon = config.icon;
                return (
                  <Button
                    key={type}
                    variant={typeFilter === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTypeFilter(type)}
                    className="h-7 text-xs gap-1"
                  >
                    <Icon className="h-3 w-3" />
                    {config.label} ({count})
                  </Button>
                );
              })}
            </div>

            {/* Results list */}
            <ScrollArea className="flex-1">
              <div className="space-y-2 pr-4">
                {isSearching ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredResults.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No results found for "{activeSearch}"
                  </div>
                ) : (
                  filteredResults.map((result) => (
                    <ResultCard
                      key={result.id}
                      result={result}
                      isExpanded={expandedIds.has(result.id)}
                      onToggle={() => toggleExpanded(result.id)}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Empty state */}
        {!activeSearch && (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-8 text-muted-foreground">
            <Search className="h-12 w-12 mb-4 opacity-20" />
            <p className="mb-2">Search the knowledge base to see what's indexed</p>
            <p className="text-xs">
              Try: "sales customers", "invoice payment", "product inventory"
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
